/**
 * «Мир» ANZONIX: публичная часть платформы.
 *
 * Человек публикует свою систему (бота) — другие видят её страницу,
 * подписываются, делают форк (свою версию) или копируют на свой
 * виртуальный счёт. Главное правило: цифры системы — только проверенные.
 * Её результат считает сервер по журналу сделок, которые он сам и
 * исполнил, а не по словам автора. Проверка на истории отдельно и с
 * пометкой: это идея, а не торговля.
 *
 * Опыт и уровень тоже считаются одинаково везде — здесь, из значков и
 * миссий профиля: терминал и сервер не могут разойтись в том, кто какого
 * уровня.
 */
import { wilsonLower } from './season.js';
/* ------------------------------------------------------------ опыт и уровни */
/** Опыт за миссию — по её id. */
export const MISSION_XP = {
    move: 100,
    radar: 100,
    alert: 100,
    idea: 100,
    indicator: 150,
    strategy: 150,
    bot: 150,
    guard: 100,
    'my-radar': 100,
    scan: 100,
};
/** Значки потруднее стоят больше: их не получить одним нажатием. */
const HEAVY_BADGES = new Set(['sharp', 'honest', 'bot-beat', 'scholar', 'cartographer', 'radar-bot']);
export function badgeXp(id) {
    return HEAVY_BADGES.has(id) ? 150 : 100;
}
/** Опыт из значков и пройденных миссий: каждое — один раз. */
export function experienceOf(badges, missions) {
    return badges.reduce((sum, id) => sum + badgeXp(id), 0) + missions.reduce((sum, id) => sum + (MISSION_XP[id] ?? 0), 0);
}
/** Сколько опыта нужно, чтобы стоять на уровне: 0, 100, 300, 600, 1000… */
export function levelFloor(level) {
    return 50 * (level - 1) * level;
}
export function levelFrom(xp) {
    let level = 1;
    while (levelFloor(level + 1) <= xp)
        level += 1;
    return { level, floor: levelFloor(level), next: levelFloor(level + 1) };
}
const TITLES = [
    [20, 'Архитектор'],
    [15, 'Создатель'],
    [10, 'Алгоритмист'],
    [7, 'Стратег'],
    [5, 'Строитель'],
    [3, 'Трейдер'],
    [1, 'Новичок'],
];
export function titleFor(level) {
    return TITLES.find(([from]) => level >= from)?.[1] ?? 'Новичок';
}
export function liveSignalStats(outcomes, from, now) {
    const mine = outcomes.filter((entry) => entry.at >= from);
    const done = mine.filter((entry) => entry.result !== 'PENDING');
    const hits = done.filter((entry) => entry.result === 'WIN').length;
    const n = done.length;
    const days = Math.max(0, Math.floor((now - from) / 86_400_000));
    return {
        signals: n,
        hits,
        pending: mine.length - n,
        rate: n > 0 ? Math.round((hits / n) * 1000) / 10 : null,
        score: Math.round(wilsonLower(hits, n) * 1000) / 10,
        days,
        ranked: n >= 30 && days >= 7,
    };
}
/** Индикатор годится в мир, если у него есть хоть одна сторона с условиями и он не на рыночном индексе. */
export function publishableSpec(spec) {
    return spec.venue !== 'index' && spec.branches.some((branch) => branch.advanced !== undefined || branch.trigger !== null || branch.context.length > 0);
}
/** Бот годится в «Мир», если он самодостаточен: конструктор или свой индикатор внутри. */
export function publishable(bot) {
    if (bot.source)
        return false;
    return bot.exit !== undefined || bot.signal !== undefined;
}
/**
 * Будит ли бот хозяина сделками: «Оповещать о сделках» включено и не на
 * паузе. Выключено — бот торгует молча, сделки видны только в его экране.
 */
export function botNotifies(bot) {
    return bot.params['notifyTrades'] === true && bot.params['notifyMuted'] !== true;
}
/** Бот повторяет сделки чужой системы — это копия, а не своя система. */
export function isMirror(bot) {
    return bot.params['mirror'] === true;
}
/**
 * Почему бот не выходит в «Мир» — словами. `null` — публиковать можно.
 *
 * Публиковать можно любого своего бота: копию сделок автора ведёт сам
 * сервер, и внутренности бота копирующему не нужны. Нельзя только копию
 * чужой системы — публикует её автор.
 */
export function unpublishableReason(bot) {
    if (typeof bot.params['copyOf'] === 'string' || isMirror(bot))
        return 'Это копия чужой системы — публикует её автор.';
    return null;
}
/**
 * Почему у системы нет форка, хотя она открытая: бот слушает радар или
 * подписку автора, и у взявшего их не будет. Копировать такую систему
 * можно — сделки повторяет сервер.
 */
export function noForkReason(bot) {
    if (publishable(bot))
        return null;
    if (bot.source?.kind === 'radar')
        return 'Бот слушает радар автора — форк появится, когда радар будет переезжать вместе с системой. Копировать можно уже сейчас.';
    if (bot.source?.kind === 'indicator')
        return 'Бот слушает подписку автора на готовый индикатор — форк появится позже. Копировать можно уже сейчас.';
    return 'Бот зависит от индикатора автора — форк появится позже. Копировать можно уже сейчас.';
}
//# sourceMappingURL=world.js.map